import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { AlertCircle, FilterX, RefreshCw, Search, SlidersHorizontal } from "lucide-react";
import { LeadStageType, ServiceType } from "@/context/LeadContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import PartnerPageHeader from "@/components/partners/PartnerPageHeader";
import PartnerLeadCard from "@/components/partners/PartnerLeadCard";
import LeadDetailsModal from "@/components/partners/LeadDetailsModal";
import { Skeleton } from "@/components/ui/skeleton";
import { FriendlyIllustration } from "@/components/ui/FriendlyIllustration";
import { usePartnerLeads } from "@/hooks/usePartnerLeads";
import { PARTNER_STAGES, PartnerOutletContext, SERVICE_LABELS, parsePortalDate } from "@/types/partnerPortal";
import { cn } from "@/lib/utils";

type SortType = "newest" | "oldest" | "priority" | "name";
const PAGE_SIZE = 24;

const PartnerLeadsPage = () => {
  const { partnerId } = useParams<{ partnerId: string }>();
  const { partner } = useOutletContext<PartnerOutletContext>();
  const { leads, isLoading, isFetching, error, refetch, updateStage, updatingLeadId, markViewed } = usePartnerLeads(partnerId);
  const [searchTerm, setSearchTerm] = useState("");
  const [stageFilter, setStageFilter] = useState<LeadStageType | "all">("all");
  const [serviceFilter, setServiceFilter] = useState<ServiceType | "all">("all");
  const [sort, setSort] = useState<SortType>("newest");
  const [unseenOnly, setUnseenOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);

  const normalizedSearch = searchTerm.trim().toLocaleLowerCase("el");
  const filteredLeads = useMemo(() => {
    const result = leads.filter((lead) => {
      const haystack = `${lead.name} ${lead.phone} ${lead.email || ""} ${SERVICE_LABELS[lead.service] || lead.service}`.toLocaleLowerCase("el");
      return (!normalizedSearch || haystack.includes(normalizedSearch))
        && (stageFilter === "all" || lead.stage === stageFilter)
        && (serviceFilter === "all" || lead.service === serviceFilter)
        && (!unseenOnly || !lead.linkOpened);
    });

    return result.sort((a, b) => {
      if (sort === "oldest") return parsePortalDate(a.createdAt).getTime() - parsePortalDate(b.createdAt).getTime();
      if (sort === "name") return a.name.localeCompare(b.name, "el");
      if (sort === "priority") {
        const score = (lead: typeof a) => (lead.stage === "Νέο" ? 50 : 0) + (!lead.linkOpened ? 30 : 0) + (lead.timeframe === "άμεσα" ? 40 : 0);
        return score(b) - score(a) || parsePortalDate(b.createdAt).getTime() - parsePortalDate(a.createdAt).getTime();
      }
      return parsePortalDate(b.createdAt).getTime() - parsePortalDate(a.createdAt).getTime();
    });
  }, [leads, normalizedSearch, stageFilter, serviceFilter, unseenOnly, sort]);

  useEffect(() => setCurrentPage(1), [normalizedSearch, stageFilter, serviceFilter, unseenOnly, sort]);

  const totalPages = Math.max(1, Math.ceil(filteredLeads.length / PAGE_SIZE));
  const pageLeads = filteredLeads.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const hasFilters = Boolean(normalizedSearch || stageFilter !== "all" || serviceFilter !== "all" || unseenOnly);

  const clearFilters = () => {
    setSearchTerm("");
    setStageFilter("all");
    setServiceFilter("all");
    setUnseenOnly(false);
  };

  return (
    <div className="space-y-6">
      <LeadDetailsModal leadId={selectedLeadId} onClose={() => setSelectedLeadId(null)} partnerId={partnerId} onViewed={markViewed} />
      <PartnerPageHeader
        eyebrow="Αρχείο επαφών"
        title="Όλα τα leads"
        description="Βρείτε γρήγορα μία επαφή, επικοινωνήστε και κρατήστε το στάδιο ενημερωμένο."
        actions={
          <Button variant="outline" onClick={() => void refetch()} disabled={isFetching} className="h-11 rounded-xl bg-white">
            <RefreshCw className={cn("mr-2 h-4 w-4", isFetching && "animate-spin")} /> Ανανέωση
          </Button>
        }
      />

      <section data-partner-tour="lead-filters" className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Όνομα, τηλέφωνο, email ή υπηρεσία…"
              className="h-11 rounded-xl border-slate-200 bg-slate-50 pl-10"
            />
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:flex">
            <Select value={stageFilter} onValueChange={(value) => setStageFilter(value as LeadStageType | "all")}>
              <SelectTrigger className="h-11 min-w-0 rounded-xl"><SelectValue placeholder="Στάδιο" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Όλα τα στάδια</SelectItem>
                {PARTNER_STAGES.map((stage) => <SelectItem key={stage.key} value={stage.key}>{stage.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={serviceFilter} onValueChange={(value) => setServiceFilter(value as ServiceType | "all")}>
              <SelectTrigger className="h-11 min-w-0 rounded-xl"><SelectValue placeholder="Υπηρεσία" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Όλες οι υπηρεσίες</SelectItem>
                {Object.entries(SERVICE_LABELS).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={(value) => setSort(value as SortType)}>
              <SelectTrigger className="col-span-2 h-11 min-w-0 rounded-xl sm:col-span-1"><SlidersHorizontal className="mr-2 h-4 w-4" /><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Νεότερα πρώτα</SelectItem>
                <SelectItem value="oldest">Παλαιότερα πρώτα</SelectItem>
                <SelectItem value="priority">Προτεραιότητα</SelectItem>
                <SelectItem value="name">Αλφαβητικά</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-3 flex flex-col gap-3 border-t border-slate-100 pt-3 sm:flex-row sm:items-center sm:justify-between">
          <label className="flex min-h-11 cursor-pointer items-center gap-2 text-sm font-medium text-slate-700">
            <Checkbox checked={unseenOnly} onCheckedChange={(checked) => setUnseenOnly(checked === true)} />
            Μόνο όσα δεν έχουν προβληθεί
          </label>
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-slate-500"><strong className="text-slate-800">{filteredLeads.length}</strong> από {leads.length} leads</p>
            {hasFilters && <Button variant="ghost" size="sm" onClick={clearFilters} className="h-10 text-slate-600"><FilterX className="mr-2 h-4 w-4" /> Καθαρισμός</Button>}
          </div>
        </div>
      </section>

      {error && (
        <div className="flex items-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm font-medium text-rose-800">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {Array.from({ length: 8 }, (_, index) => <Skeleton key={index} className="h-72 rounded-2xl" />)}
        </div>
      ) : pageLeads.length ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {pageLeads.map((lead) => (
              <PartnerLeadCard key={lead.id} lead={lead} partnerName={partner.name} onOpen={setSelectedLeadId} onStageChange={(id, stage) => void updateStage(id, stage)} updating={updatingLeadId === lead.id} />
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex flex-col items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-3 sm:flex-row">
              <p className="text-sm text-slate-600">Σελίδα <strong>{currentPage}</strong> από {totalPages}</p>
              <div className="grid w-full grid-cols-2 gap-2 sm:w-auto">
                <Button variant="outline" onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={currentPage === 1} className="h-11 rounded-xl">Προηγούμενη</Button>
                <Button variant="outline" onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))} disabled={currentPage === totalPages} className="h-11 rounded-xl">Επόμενη</Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-5 py-14 text-center">
          <FriendlyIllustration kind="lead-flow" className="mx-auto h-36 w-auto max-w-[230px] drop-shadow-[0_12px_20px_rgba(14,116,190,0.12)]" />
          <h2 className="mt-2 font-bold text-slate-900">{hasFilters ? "Δεν βρέθηκαν leads" : "Δεν υπάρχουν leads ακόμη"}</h2>
          <p className="mx-auto mt-1 max-w-sm text-sm leading-6 text-slate-500">
            {hasFilters ? "Δοκιμάστε διαφορετική αναζήτηση ή καθαρίστε τα φίλτρα." : "Μόλις γίνει νέα ανάθεση, θα εμφανιστεί αυτόματα εδώ."}
          </p>
          {hasFilters && <Button variant="outline" onClick={clearFilters} className="mt-4 h-11 rounded-xl">Καθαρισμός φίλτρων</Button>}
        </div>
      )}
    </div>
  );
};

export default PartnerLeadsPage;
